import {
  FC,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { trackAssistantEvent } from "../../utils/assistant-analytics"
import { Link, useLocation } from "react-router-dom"
import { DateTime } from "luxon"
import {
  ArrowUpRight,
  AtSign,
  BellRing,
  Building2,
  CircleSlash,
  DollarSign,
  Landmark,
  Loader2,
  LucideIcon,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  PinOff,
  Plus,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  Users,
  Wrench,
  X
} from "lucide-react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useAppDispatch } from "../../hooks/use-store"
import { useIsSuperAdmin } from "../../hooks/use-user-roles"
import { setStaffAiDrawerOpen } from "../../../redux/slices/app"
import {
  StaffConversationSummary,
  deleteStaffConversation,
  listStaffConversations,
  pinStaffConversation
} from "../../../api/staff-agent"
import {
  StaffChatMessage,
  StaffFormRequest,
  StaffToolEvent
} from "../../hooks/use-staff-chat-stream"
import ApprovalCard from "./approval-card"
import ChartBlock from "./chart-block"
import { getAssistantCopy } from "./copy"
import BriefingGreeting from "./briefing-greeting"
import StarterChips from "./starter-chips"
import FormCard from "./form-card"
import { useStaffChatSession } from "./session-context"
import FeedbackControls from "./feedback-controls"

const isChartElement = (node: any): boolean => {
  const child = Array.isArray(node) ? node[0] : node
  return Boolean(child?.props?.className?.includes("language-chart"))
}

const PROSE_CLASSES = [
  "prose prose-sm max-w-none break-words",
  "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5",
  "prose-headings:mt-3 prose-headings:mb-1",
  "prose-pre:bg-neutral-200 prose-pre:rounded-lg prose-pre:p-3",
  "prose-code:text-xs prose-code:bg-neutral-200 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
].join(" ")

/** One chip per tool name: repeat calls collapse into a ×N count with rows summed. */
interface ToolChipGroup {
  name: string
  count: number
  running: boolean
  failed: boolean
  rowCount?: number
}

const groupToolEvents = (events: StaffToolEvent[]): ToolChipGroup[] => {
  const byName = new Map<string, ToolChipGroup>()
  for (const tool of events) {
    let group = byName.get(tool.name)
    if (!group) {
      group = { name: tool.name, count: 0, running: false, failed: false }
      byName.set(tool.name, group)
    }
    group.count += 1
    group.running = group.running || tool.running
    group.failed = group.failed || tool.ok === false
    if (typeof tool.rowCount === "number") {
      group.rowCount = (group.rowCount ?? 0) + tool.rowCount
    }
  }
  return Array.from(byName.values())
}

const ToolChip: FC<{ group: ToolChipGroup }> = ({ group }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-px text-[11px] font-medium ${
      group.running
        ? "border-violet-200 bg-violet-50 text-violet-700"
        : group.failed
        ? "border-red-200 bg-red-50 text-red-600"
        : "border-gray-200 bg-gray-50 text-gray-600"
    }`}
  >
    {group.running ? (
      <Loader2 size={11} className="animate-spin" />
    ) : (
      <Wrench size={11} />
    )}
    {group.name}
    {group.count > 1 && <span className="text-gray-400">×{group.count}</span>}
    {typeof group.rowCount === "number" && (
      <span className="text-gray-400">· {group.rowCount} rows</span>
    )}
  </span>
)

interface LinkTypeStyle {
  label: string
  icon: LucideIcon
  classes: string
}

/**
 * Registry of deep-linkable entity types: route prefix → chip styling.
 * Doubles as the whitelist (mirrors the catalog in bb-admin-agent's system
 * prompt) — a path whose first segment isn't here renders as plain text.
 */
const LINK_TYPE_REGISTRY: Record<string, LinkTypeStyle> = {
  "pricing-log": {
    label: "Pricing log",
    icon: Monitor,
    classes:
      "bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-violet-800"
  },
  leads: {
    label: "Lead",
    icon: AtSign,
    classes:
      "bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
  },
  brands: {
    label: "Brand",
    icon: Landmark,
    classes:
      "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
  },
  "loan-officers": {
    label: "Loan officer",
    icon: Users,
    classes: "bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
  },
  engagements: {
    label: "Subscription",
    icon: BellRing,
    classes: "bg-sky-50 text-sky-700 hover:bg-sky-100 hover:text-sky-800"
  },
  organizations: {
    label: "Organization",
    icon: Building2,
    classes:
      "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800"
  },
  "dream-rate": {
    label: "Dream Rate",
    icon: DollarSign,
    classes: "bg-rose-50 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
  }
}

const CHIP_BASE_CLASSES =
  "inline-flex items-baseline gap-1 rounded px-1 py-px font-medium no-underline transition-colors"

/** Deep-link types only super admins can navigate to (route gated by role). */
const SUPER_ADMIN_ONLY_LINK_TYPES = new Set(["organizations"])

const ChatLink: FC<{
  href?: string
  children?: React.ReactNode
  onInternalNavigate?: () => void
}> = ({ href = "", children, onInternalNavigate }) => {
  const isSuperAdmin = useIsSuperAdmin()
  const internalMatch = href.match(/^\/([a-z-]+)(\/[\w-]+)*\/?$/)
  let linkType = internalMatch
    ? LINK_TYPE_REGISTRY[internalMatch[1]]
    : undefined
  if (
    internalMatch &&
    SUPER_ADMIN_ONLY_LINK_TYPES.has(internalMatch[1]) &&
    !isSuperAdmin
  ) {
    linkType = undefined
  }
  if (linkType) {
    const Icon = linkType.icon
    return (
      <Link
        to={href}
        onClick={onInternalNavigate}
        title={linkType.label}
        className={`${CHIP_BASE_CLASSES} ${linkType.classes}`}
      >
        <Icon size={11} className="self-center" aria-hidden="true" />
        {children}
      </Link>
    )
  }
  if (/^https?:\/\//.test(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`${CHIP_BASE_CLASSES} bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-violet-800`}
      >
        {children}
        <ArrowUpRight size={11} className="self-center" aria-hidden="true" />
      </a>
    )
  }
  // Unknown/invented paths: render the text without a broken link
  return <span>{children}</span>
}

const SUBMISSION_RE = /^Form submission "([^"]+)":\n```json\n([\s\S]+)\n```$/

/** Compact rendering for the JSON message a form submission sends back. */
const SubmissionSummary: FC<{ title: string; json: string }> = ({
  title,
  json
}) => {
  let entries: [string, unknown][] = []
  try {
    entries = Object.entries(JSON.parse(json))
  } catch {
    return <p className="whitespace-pre-wrap break-words text-sm">{json}</p>
  }
  return (
    <div>
      <p className="text-sm font-semibold">{title}</p>
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-xs text-violet-100 whitespace-nowrap">{key}</dt>
            <dd className="text-xs break-all">
              {Array.isArray(value)
                ? value.join(", ")
                : value === null
                ? "—"
                : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

// Memoized so typing in the input (which re-renders the panel per keystroke)
// doesn't re-run react-markdown / Highcharts for every message
const MessageRow = memo<{
  message: StaffChatMessage
  isStreaming: boolean
  onActionStatusChange: (actionId: string, status: string) => void
  onFormSubmit: (
    form: StaffFormRequest,
    values: Record<string, unknown>
  ) => void
  onInternalNavigate?: () => void
  onRetry?: () => void
  conversationId?: string
}>(function MessageRow({
  message,
  isStreaming,
  onActionStatusChange,
  onFormSubmit,
  onInternalNavigate,
  onRetry,
  conversationId
}) {
  const isUser = message.role === "user"
  const submission = isUser ? message.content.match(SUBMISSION_RE) : null
  const isEmptyStreamingStub =
    !isUser &&
    isStreaming &&
    !message.content &&
    message.toolEvents.length === 0 &&
    !message.error

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`text-black min-w-0 max-w-[85%] overflow-hidden rounded-2xl px-4 py-2.5 ${
          isUser ? "bg-zinc-200" : "border border-gray-200 bg-white shadow-sm"
        }`}
      >
        {message.toolEvents.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1">
            {groupToolEvents(message.toolEvents).map(group => (
              <ToolChip key={group.name} group={group} />
            ))}
          </div>
        )}
        {isUser ? (
          submission ? (
            <SubmissionSummary title={submission[1]} json={submission[2]} />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm">
              {message.content}
            </p>
          )
        ) : isEmptyStreamingStub ? (
          <Loader2 size={16} className="my-1 animate-spin text-violet-600" />
        ) : (
          <div className={PROSE_CLASSES}>
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <ChatLink href={href} onInternalNavigate={onInternalNavigate}>
                    {children}
                  </ChatLink>
                ),
                // Wide tables scroll inside the bubble instead of blowing it out
                table: ({ children }) => (
                  <div className="overflow-x-auto">
                    <table>{children}</table>
                  </div>
                ),
                // ```chart fences render as Highcharts (chart-block.tsx);
                // pre unwraps so prose code styling doesn't frame the chart
                pre: ({ children, ...props }) =>
                  isChartElement(children) ? (
                    <>{children}</>
                  ) : (
                    <pre {...props}>{children}</pre>
                  ),
                code: ({ className, children, ...props }) =>
                  className?.includes("language-chart") ? (
                    <ChartBlock
                      source={String(children)}
                      isStreaming={isStreaming}
                    />
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  )
              }}
            >
              {message.content}
            </Markdown>
          </div>
        )}
        {message.pendingActions.map(action => (
          <ApprovalCard
            key={action.id}
            action={action}
            onStatusChange={onActionStatusChange}
          />
        ))}
        {message.formRequests.map(form => (
          <FormCard
            key={form.id}
            form={form}
            disabled={isStreaming}
            onSubmit={onFormSubmit}
          />
        ))}
        {message.error && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-red-600">{message.error}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
              >
                <RotateCcw size={13} /> Try again
              </button>
            )}
          </div>
        )}
        {!isUser &&
          !isStreaming &&
          !message.error &&
          message.feedbackTargetId && (
            <FeedbackControls
              targetId={message.feedbackTargetId}
              conversationId={conversationId}
              initial={message.feedback}
            />
          )}
      </div>
    </div>
  )
})

/**
 * Follow-up question chips for the most recent assistant answer (from the
 * server's `suggestions` SSE event). Rendered only under the last message and
 * only between turns, so stale chips never linger once a new question is sent.
 */
const FollowupChips: FC<{
  messages: StaffChatMessage[]
  isStreaming: boolean
  onPick: (text: string) => void
}> = ({ messages, isStreaming, onPick }) => {
  const last = messages[messages.length - 1]
  if (
    isStreaming ||
    !last ||
    last.role !== "assistant" ||
    last.error ||
    !last.suggestions?.length
  ) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-2">
      {last.suggestions.map(suggestion => (
        <button
          key={suggestion}
          type="button"
          onClick={() => onPick(suggestion)}
          className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-sm text-gray-600 transition-colors hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
        >
          {suggestion}
        </button>
      ))}
    </div>
  )
}

const HistoryItem: FC<{
  conversation: StaffConversationSummary
  isActive: boolean
  onSelect: () => void
  onTogglePin: () => void
  onDelete: () => void
}> = ({ conversation, isActive, onSelect, onTogglePin, onDelete }) => (
  <div
    className={`group relative rounded-lg transition-colors ${
      isActive ? "bg-violet-50" : "hover:bg-gray-100"
    }`}
  >
    <button
      type="button"
      onClick={onSelect}
      className="w-full px-3 py-2 pr-14 text-left"
    >
      <p
        className={`flex items-center gap-1 truncate text-sm ${
          isActive ? "font-medium text-violet-800" : "text-neutral-700"
        }`}
      >
        {conversation.pinned && (
          <Pin size={12} className="shrink-0 text-violet-600" />
        )}
        <span className="truncate">{conversation.title}</span>
      </p>
      <p className="mt-0.5 text-xs text-neutral-400">
        {DateTime.fromISO(conversation.updatedAt).toRelative()} ·{" "}
        {conversation.messageCount} msgs
      </p>
    </button>
    <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5 sm:hidden sm:group-hover:flex sm:group-focus-within:flex">
      <button
        type="button"
        onClick={onTogglePin}
        title={conversation.pinned ? "Unpin" : "Pin to top"}
        className="rounded p-1 text-neutral-400 transition-colors hover:bg-violet-50 hover:text-violet-700"
      >
        {conversation.pinned ? <PinOff size={14} /> : <Pin size={14} />}
      </button>
      <button
        type="button"
        onClick={onDelete}
        title="Delete conversation"
        className="rounded p-1 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 size={14} />
      </button>
    </div>
  </div>
)

interface StaffAiChatProps {
  /** "page" = framed card on /staff-ai; "drawer" = fills the slideout */
  variant?: "page" | "drawer"
}

const StaffAiChat: FC<StaffAiChatProps> = ({ variant = "page" }) => {
  // Drawer opens are tracked in drawer.tsx; the full page tracks its mount.
  useEffect(() => {
    if (variant === "page") {
      trackAssistantEvent("assistant_opened", { surface: "page" })
    }
  }, [variant])

  const dispatch = useAppDispatch()
  const location = useLocation()
  const isSuperAdmin = useIsSuperAdmin()
  const copy = getAssistantCopy(isSuperAdmin)
  // Page context for the agent ("this quote", "this brand"...). The panel's
  // own route carries no signal, so only send real pages.
  const currentPage = location.pathname.startsWith("/staff-ai")
    ? undefined
    : `${location.pathname}${location.search}`
  const {
    messages,
    isStreaming,
    conversationId,
    sendMessage,
    stop,
    retryLast,
    resetConversation,
    loadConversation,
    markFormSubmitted,
    updatePendingActionStatus
  } = useStaffChatSession()
  const [input, setInput] = useState("")
  const [history, setHistory] = useState<StaffConversationSummary[]>([])
  const [historyNote, setHistoryNote] = useState<string | null>(null)
  const [historyCollapsed, setHistoryCollapsed] = useState(
    () => localStorage.getItem("staffAiHistoryCollapsed") === "1"
  )
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false)

  const toggleHistory = (collapsed: boolean) => {
    setHistoryCollapsed(collapsed)
    localStorage.setItem("staffAiHistoryCollapsed", collapsed ? "1" : "0")
  }
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const prevStreamingRef = useRef(false)
  const [isAtBottom, setIsAtBottom] = useState(true)

  useEffect(() => {
    const el = scrollRef.current
    if (el && isAtBottom) el.scrollTop = el.scrollHeight
  }, [messages, isAtBottom])

  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`
  }, [input])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80)
  }

  const scrollToLatest = () => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
    setIsAtBottom(true)
  }

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await listStaffConversations())
      setHistoryNote(null)
    } catch (error: any) {
      setHistoryNote(error?.message || "History unavailable")
    }
  }, [])

  useEffect(() => {
    refreshHistory()
  }, [refreshHistory])

  // A finished stream means a new/updated session was persisted
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming) refreshHistory()
    prevStreamingRef.current = isStreaming
  }, [isStreaming, refreshHistory])

  const selectConversation = (id: string) => {
    if (id === conversationId) return
    trackAssistantEvent("conversation_resumed", { surface: variant })
    loadConversation(id).catch((error: any) =>
      setHistoryNote(error?.message || "Failed to load conversation")
    )
  }

  // Following a deep link from the drawer should reveal the page behind it.
  // Stable references so the memoized MessageRow doesn't re-render per keystroke.
  const closeDrawer = useCallback(
    () => dispatch(setStaffAiDrawerOpen(false)),
    [dispatch]
  )
  const onInternalNavigate = variant === "drawer" ? closeDrawer : undefined

  const submitForm = useCallback(
    (form: StaffFormRequest, values: Record<string, unknown>) => {
      markFormSubmitted(form.id)
      trackAssistantEvent("form_submitted", { surface: variant })
      sendMessage(
        `Form submission "${form.title}":\n\`\`\`json\n${JSON.stringify(
          values,
          null,
          2
        )}\n\`\`\``,
        currentPage
      )
    },
    [markFormSubmitted, sendMessage, currentPage, variant]
  )

  const togglePin = async (conversation: StaffConversationSummary) => {
    try {
      await pinStaffConversation(conversation.id, !conversation.pinned)
      refreshHistory()
    } catch (error: any) {
      setHistoryNote(error?.message || "Failed to update pin")
    }
  }

  const removeConversation = async (id: string) => {
    if (!window.confirm("Delete this conversation? This cannot be undone.")) {
      return
    }
    try {
      await deleteStaffConversation(id)
      if (id === conversationId) resetConversation()
      refreshHistory()
    } catch (error: any) {
      setHistoryNote(error?.message || "Failed to delete conversation")
    }
  }

  const submit = (text?: string) => {
    const value = (text ?? input).trim()
    if (!value || isStreaming) return
    setInput("")
    setIsAtBottom(true)
    trackAssistantEvent("message_sent", { surface: variant })
    sendMessage(value, currentPage)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div
      className={`relative flex overflow-hidden bg-gray-50/60 ${
        variant === "drawer"
          ? "h-full"
          : "h-[calc(100vh-16rem)] min-h-[24rem] rounded-2xl border border-gray-200 shadow-sm"
      }`}
    >
      <aside
        className={`hidden shrink-0 flex-col border-r border-gray-200 bg-white transition-all duration-200 md:flex ${
          historyCollapsed ? "w-12" : "w-64"
        }`}
      >
        {historyCollapsed ? (
          <div className="flex flex-col items-center gap-1 p-2">
            <button
              type="button"
              onClick={() => toggleHistory(false)}
              title="Show history"
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
            >
              <PanelLeftOpen size={16} />
            </button>
            <button
              type="button"
              onClick={resetConversation}
              title="New chat"
              className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-violet-50 hover:text-violet-700"
            >
              <Plus size={16} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1.5 p-3">
              <button
                type="button"
                onClick={resetConversation}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-violet-200 hover:bg-violet-50 hover:text-violet-700"
              >
                <Plus size={16} />
                New chat
              </button>
              <button
                type="button"
                onClick={() => toggleHistory(true)}
                title="Hide history"
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
            <div className="flex-1 space-y-1 overflow-y-auto px-2 pb-2">
              {historyNote ? (
                <p className="px-3 py-2 text-xs text-neutral-400">
                  {historyNote}
                </p>
              ) : history.length === 0 ? (
                <p className="px-3 py-2 text-xs text-neutral-400">
                  No conversations yet
                </p>
              ) : (
                history.map(conversation => (
                  <HistoryItem
                    key={conversation.id}
                    conversation={conversation}
                    isActive={conversation.id === conversationId}
                    onSelect={() => selectConversation(conversation.id)}
                    onTogglePin={() => togglePin(conversation)}
                    onDelete={() => removeConversation(conversation.id)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </aside>

      {mobileHistoryOpen && (
        <aside className="absolute inset-0 z-20 flex flex-col bg-white md:hidden">
          <div className="flex items-center gap-2 border-b border-gray-200 p-3">
            <button
              type="button"
              onClick={() => {
                resetConversation()
                setMobileHistoryOpen(false)
              }}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600"
            >
              <Plus size={16} /> New chat
            </button>
            <button
              type="button"
              onClick={() => setMobileHistoryOpen(false)}
              title="Close history"
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 space-y-1 overflow-y-auto p-2">
            {historyNote ? (
              <p className="px-3 py-2 text-xs text-neutral-400">
                {historyNote}
              </p>
            ) : history.length === 0 ? (
              <p className="px-3 py-2 text-xs text-neutral-400">
                No conversations yet
              </p>
            ) : (
              history.map(conversation => (
                <HistoryItem
                  key={conversation.id}
                  conversation={conversation}
                  isActive={conversation.id === conversationId}
                  onSelect={() => {
                    selectConversation(conversation.id)
                    setMobileHistoryOpen(false)
                  }}
                  onTogglePin={() => togglePin(conversation)}
                  onDelete={() => removeConversation(conversation.id)}
                />
              ))
            )}
          </div>
        </aside>
      )}

      <div className="relative flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            <PanelLeftOpen size={16} /> History
          </button>
          <button
            type="button"
            onClick={resetConversation}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-violet-700 hover:bg-violet-50"
          >
            <Plus size={16} /> New chat
          </button>
        </div>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 space-y-3 overflow-y-auto overflow-x-hidden p-4"
        >
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-50 text-violet-900 ring-1 ring-violet-100">
                <Sparkles size={22} />
              </div>
              <div>
                <p className="font-medium text-neutral-800">
                  {copy.emptyTitle}
                </p>
                <p className="mt-1 text-sm text-neutral-500">
                  {copy.emptySubtitle}
                </p>
              </div>
              <BriefingGreeting onInvestigate={submit} />
              <StarterChips
                page={currentPage}
                onPick={message => {
                  trackAssistantEvent("suggestion_clicked", {
                    surface: variant,
                    kind: "starter"
                  })
                  submit(message)
                }}
              />
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <MessageRow
                  key={message.id}
                  message={message}
                  isStreaming={isStreaming}
                  onActionStatusChange={updatePendingActionStatus}
                  onFormSubmit={submitForm}
                  onInternalNavigate={onInternalNavigate}
                  onRetry={
                    index === messages.length - 1 && message.error
                      ? retryLast
                      : undefined
                  }
                  conversationId={conversationId}
                />
              ))}
              <FollowupChips
                messages={messages}
                isStreaming={isStreaming}
                onPick={text => {
                  trackAssistantEvent("suggestion_clicked", {
                    surface: variant
                  })
                  submit(text)
                }}
              />
            </>
          )}
        </div>

        {!isAtBottom && messages.length > 0 && (
          <button
            type="button"
            onClick={scrollToLatest}
            className="absolute bottom-20 right-5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-md transition-colors hover:border-violet-200 hover:text-violet-700"
          >
            Jump to latest
          </button>
        )}

        <div className="border-t border-gray-200 bg-white p-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask the BB assistant…"
              className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            {isStreaming ? (
              <button
                type="button"
                onClick={stop}
                title="Stop generating"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50"
              >
                <CircleSlash size={18} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => submit()}
                disabled={!input.trim()}
                title="Send"
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600 text-white transition-colors hover:bg-violet-700 disabled:opacity-40"
              >
                <Send size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StaffAiChat
